import InternalCommunicationAPI from 'dashboard/api/internalCommunication';

export const state = {
  rooms: [],
  messages: {},
  activeRoomId: null,
  hasMore: {},
  uiFlags: {
    isFetchingRooms: false,
    isFetchingMessages: false,
    isSending: false,
  },
};

export const getters = {
  getRooms: currentState => currentState.rooms,
  getRoom: currentState => roomId =>
    currentState.rooms.find(room => Number(room.id) === Number(roomId)),
  getMessages: currentState => roomId => currentState.messages[roomId] || [],
  getHasMore: currentState => roomId => currentState.hasMore[roomId] || false,
  getUnreadCount: currentState =>
    currentState.rooms.reduce(
      (total, room) => total + Number(room.unread_count || 0),
      0
    ),
  getUIFlags: currentState => currentState.uiFlags,
};

export const mutations = {
  SET_ROOMS(currentState, rooms) {
    currentState.rooms = rooms;
  },
  UPSERT_ROOM(currentState, room) {
    const index = currentState.rooms.findIndex(
      item => Number(item.id) === Number(room.id)
    );
    if (index === -1) {
      currentState.rooms = [room, ...currentState.rooms];
    } else {
      currentState.rooms.splice(index, 1, {
        ...currentState.rooms[index],
        ...room,
      });
    }
  },
  REMOVE_ROOM(currentState, roomId) {
    currentState.rooms = currentState.rooms.filter(
      room => Number(room.id) !== Number(roomId)
    );
    delete currentState.messages[roomId];
  },
  SET_MESSAGES(currentState, { roomId, messages, prepend = false }) {
    const existing = currentState.messages[roomId] || [];
    currentState.messages[roomId] = prepend
      ? [...messages, ...existing]
      : messages;
  },
  UPSERT_MESSAGE(currentState, message) {
    const roomMessages = currentState.messages[message.room_id] || [];
    const index = roomMessages.findIndex(
      item =>
        Number(item.id) === Number(message.id) ||
        (message.client_id && item.client_id === message.client_id)
    );
    if (index === -1) {
      currentState.messages[message.room_id] = [...roomMessages, message];
    } else {
      const reconciledMessage = { ...roomMessages[index], ...message };
      if (!message.optimistic) delete reconciledMessage.optimistic;
      roomMessages.splice(index, 1, reconciledMessage);
      currentState.messages[message.room_id] = [...roomMessages];
    }
  },
  REMOVE_OPTIMISTIC_MESSAGE(currentState, { roomId, clientId }) {
    currentState.messages[roomId] = (
      currentState.messages[roomId] || []
    ).filter(message => message.client_id !== clientId || !message.optimistic);
  },
  SET_ACTIVE_ROOM(currentState, roomId) {
    currentState.activeRoomId = roomId;
  },
  SET_HAS_MORE(currentState, { roomId, hasMore }) {
    currentState.hasMore[roomId] = hasMore;
  },
  SET_UI_FLAG(currentState, flags) {
    currentState.uiFlags = { ...currentState.uiFlags, ...flags };
  },
  SET_ROOM_UNREAD(currentState, { roomId, unreadCount }) {
    const room = currentState.rooms.find(
      item => Number(item.id) === Number(roomId)
    );
    if (room) room.unread_count = unreadCount;
  },
  INCREMENT_ROOM_UNREAD(currentState, roomId) {
    const room = currentState.rooms.find(
      item => Number(item.id) === Number(roomId)
    );
    if (room) room.unread_count = Number(room.unread_count || 0) + 1;
  },
  RESET(currentState) {
    currentState.rooms = [];
    currentState.messages = {};
    currentState.activeRoomId = null;
    currentState.hasMore = {};
  },
};

export const actions = {
  async fetchRooms({ commit }) {
    commit('SET_UI_FLAG', { isFetchingRooms: true });
    try {
      const { data } = await InternalCommunicationAPI.getRooms();
      commit('SET_ROOMS', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isFetchingRooms: false });
    }
  },
  async fetchRoom({ commit }, roomId) {
    const { data } = await InternalCommunicationAPI.getRoom(roomId);
    commit('UPSERT_ROOM', data);
    return data;
  },
  async fetchMessages({ commit }, { roomId, beforeSequence, prepend = false }) {
    commit('SET_UI_FLAG', { isFetchingMessages: true });
    try {
      const { data } = await InternalCommunicationAPI.getMessages(
        roomId,
        beforeSequence
      );
      commit('SET_MESSAGES', { roomId, messages: data.messages, prepend });
      commit('SET_HAS_MORE', { roomId, hasMore: data.meta.has_more });
      commit('SET_ACTIVE_ROOM', roomId);
      return data.messages;
    } finally {
      commit('SET_UI_FLAG', { isFetchingMessages: false });
    }
  },
  async createRoom({ commit }, room) {
    const { data } = await InternalCommunicationAPI.createRoom(room);
    commit('UPSERT_ROOM', data);
    return data;
  },
  async archiveRoom({ commit }, roomId) {
    await InternalCommunicationAPI.archiveRoom(roomId);
    commit('REMOVE_ROOM', roomId);
  },
  async leaveRoom({ commit }, roomId) {
    await InternalCommunicationAPI.leaveRoom(roomId);
    commit('REMOVE_ROOM', roomId);
  },
  async replaceMembers(
    { dispatch, getters: currentGetters },
    { roomId, userIds }
  ) {
    const room = currentGetters.getRoom(roomId);
    const existingIds = room.members.map(member => Number(member.id));
    const requestedIds = userIds.map(Number);
    const addedIds = requestedIds.filter(id => !existingIds.includes(id));
    const removedIds = existingIds.filter(id => !requestedIds.includes(id));

    if (addedIds.length) {
      await InternalCommunicationAPI.addMembers(roomId, addedIds);
    }
    await Promise.all(
      removedIds.map(userId =>
        InternalCommunicationAPI.removeMember(roomId, userId)
      )
    );
    await dispatch('fetchRooms');
  },
  async sendMessage({ commit }, { roomId, message }) {
    commit('SET_UI_FLAG', { isSending: true });
    const optimistic = {
      id: `pending-${message.client_id}`,
      room_id: roomId,
      sequence: Number.MAX_SAFE_INTEGER,
      client_id: message.client_id,
      content: message.content,
      created_at: new Date().toISOString(),
      optimistic: true,
      attachments: [],
      author: {},
    };
    commit('UPSERT_MESSAGE', optimistic);
    try {
      const { data } = await InternalCommunicationAPI.createMessage(
        roomId,
        message
      );
      commit('UPSERT_MESSAGE', data);
      return data;
    } catch (error) {
      commit('REMOVE_OPTIMISTIC_MESSAGE', {
        roomId,
        clientId: message.client_id,
      });
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isSending: false });
    }
  },
  async updateMessage({ commit }, { roomId, messageId, message }) {
    const { data } = await InternalCommunicationAPI.updateMessage(
      roomId,
      messageId,
      message
    );
    commit('UPSERT_MESSAGE', data);
    return data;
  },
  async deleteMessage({ commit }, { roomId, messageId, reason }) {
    const { data } = await InternalCommunicationAPI.deleteMessage(
      roomId,
      messageId,
      reason
    );
    commit('UPSERT_MESSAGE', data);
  },
  async markRead({ commit }, roomId) {
    await InternalCommunicationAPI.markRead(roomId);
    commit('SET_ROOM_UNREAD', { roomId, unreadCount: 0 });
  },
  async handleMessageEvent({ commit, state: currentState, dispatch }, message) {
    commit('UPSERT_MESSAGE', message);
    if (Number(currentState.activeRoomId) === Number(message.room_id)) {
      await dispatch('markRead', message.room_id);
    } else {
      commit('INCREMENT_ROOM_UNREAD', message.room_id);
    }
  },
  handleRoomEvent({ commit }, room) {
    commit('UPSERT_ROOM', room);
  },
  handleMembershipEvent({ commit }, payload) {
    if (!payload.active) commit('REMOVE_ROOM', payload.room_id);
  },
  reset({ commit }) {
    commit('RESET');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
